import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { jobsAPI, projectsAPI } from '@/services/api';
import { useI18n } from '@/contexts/I18nContext';
import { AlertTriangle, Eye, CheckCircle } from 'lucide-react';
import JobCompletionDialog from '@/components/JobCompletionDialog';

const formatStartedSince = (isoString) => {
  if (!isoString) return null;
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString('is-IS', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch { return null; }
};

const DailyWorkLauncher = () => {
  const { t } = useI18n();
  const [jobs, setJobs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [completedJob, setCompletedJob] = useState(null);

  useEffect(() => {
    jobsAPI.my()
      .then(res => setJobs(res.data || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const handleDone = async (job) => {
    try {
      await projectsAPI.updateJob(job.project_id, job.job_id, { status: 'done', completed: true });
      setCompletedJob(job);
      setJobs(prev => prev.filter(j => j.job_id !== job.job_id));
    } catch { /* ignore */ }
  };

  if (!loaded) return null;

  const overdue = jobs.filter(j => j.is_overdue);
  const dueToday = jobs.filter(j => j.is_due_today && !j.is_overdue);
  const inProgress = jobs.filter(j => j.status === 'in_progress' && !j.is_overdue && !j.is_due_today);
  const totalToday = overdue.length + dueToday.length;

  // Show "no tasks today" empty state when loaded but nothing actionable
  if (totalToday === 0 && overdue.length === 0 && inProgress.length === 0) {
    if (completedJob) {
      return (
        <JobCompletionDialog job={completedJob} onClose={() => setCompletedJob(null)} />
      );
    }
    return (
      <section className="card border-l-4 border-l-emerald-500/40 p-4" data-testid="daily-work-launcher-empty">
        <div className="flex items-center gap-2">
          <CheckCircle size={14} className="text-emerald-400" />
          <p className="text-sm text-foreground-muted">{t('dashboard.daily_empty')}</p>
        </div>
      </section>
    );
  }

  // Prioritize active in_progress job, then overdue, then today
  const activeInProgress = [...overdue, ...dueToday, ...inProgress].find(j => j.status === 'in_progress');
  const allActionable = [...overdue, ...dueToday, ...inProgress];
  const withTime = allActionable.filter(j => j.planned_start_time);
  const nextOpen = allActionable.find(j => j.status === 'open');
  const nextJob = activeInProgress
    || (withTime.length > 0
      ? withTime.sort((a, b) => (a.planned_start_time || '').localeCompare(b.planned_start_time || ''))[0]
      : allActionable[0]);

  // Urgency: red if overdue, amber if next job starts within 2h, green otherwise
  let urgency = 'green';
  if (overdue.length > 0) {
    urgency = 'red';
  } else if (nextJob?.planned_start_time && nextJob.status !== 'in_progress') {
    const now = new Date();
    const [h, m] = nextJob.planned_start_time.split(':').map(Number);
    const jobStart = new Date(now);
    jobStart.setHours(h, m, 0, 0);
    const diffMs = jobStart - now;
    if (diffMs > 0 && diffMs < 2 * 60 * 60 * 1000) urgency = 'amber';
  }

  const urgencyBorder = urgency === 'red'
    ? 'border-l-red-500'
    : urgency === 'amber'
      ? 'border-l-amber-500'
      : 'border-l-emerald-500';

  const urgencyDot = urgency === 'red'
    ? 'bg-red-500'
    : urgency === 'amber'
      ? 'bg-amber-500'
      : 'bg-emerald-500';

  const isActive = false; // Simplified: no start state

  return (
    <>
      <section
        className={`card border-l-4 ${urgencyBorder} p-4`}
        data-testid="daily-work-launcher"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${urgencyDot}`} data-testid="urgency-dot" />
            <h2 className="text-sm font-semibold" data-testid="daily-title">
              {t('dashboard.daily_title')} ({totalToday})
            </h2>
          </div>
          <div className="flex items-center gap-3 text-xs">
            {overdue.length > 0 && (
              <span className="text-red-400 font-medium" data-testid="daily-overdue-count">
                {overdue.length} {t('dashboard.daily_overdue')}
              </span>
            )}
            {dueToday.length > 0 && (
              <span className="text-foreground-muted" data-testid="daily-today-count">
                {dueToday.length} {t('dashboard.daily_today')}
              </span>
            )}
          </div>
        </div>

        {/* Active / Next job preview */}
        {nextJob && (
          <div className={`flex items-center gap-3 mb-3 ${isActive ? 'bg-accent/5 -mx-4 px-4 py-2 border-y border-accent/10' : ''}`} data-testid="daily-next-job">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium truncate" data-testid="daily-next-title">{nextJob.title}</p>
                {isActive && (
                  <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-accent/15 text-accent font-medium" data-testid="daily-active-badge">
                    {t('dashboard.daily_in_progress')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground-muted mt-0.5">
                <span>{nextJob.project_name}</span>
                {isActive && nextJob.started_at ? (
                  <span className="font-mono font-medium text-accent" data-testid="daily-started-since">
                    {t('dashboard.daily_since')} {formatStartedSince(nextJob.started_at)}
                  </span>
                ) : nextJob.planned_start_time && nextJob.planned_end_time ? (
                  <span className="font-mono font-medium text-foreground-secondary">
                    {nextJob.planned_start_time} - {nextJob.planned_end_time}
                  </span>
                ) : null}
                {nextJob.is_overdue && (
                  <span className="inline-flex items-center gap-0.5 text-red-400">
                    <AlertTriangle size={10} />
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          <Link
            to="/projects"
            className="flex-1 btn btn-sm bg-card-hover text-foreground border border-border hover:bg-border/50 text-xs justify-center gap-1.5"
            data-testid="daily-view-btn"
          >
            <Eye size={13} />
            {t('dashboard.daily_view_projects')}
          </Link>
          {nextJob && (
            <button
              onClick={() => handleDone(nextJob)}
              className="flex-1 btn btn-sm bg-emerald-500 text-white hover:bg-emerald-600 text-xs justify-center gap-1.5"
              data-testid="daily-done-btn"
            >
              <CheckCircle size={13} />
              {t('dashboard.daily_mark_done')}
            </button>
          )}
        </div>
      </section>

      {completedJob && (
        <JobCompletionDialog job={completedJob} onClose={() => setCompletedJob(null)} />
      )}
    </>
  );
};

export default DailyWorkLauncher;
